export const MODULE_KEYS = [
  "dashboard",
  "students",
  "teachers",
  "classes",
  "results",
  "report_cards",
  "attendance",
  "fees",
  "payroll",
  "messaging",
  "uniform_orders",
  "library",
  "expenses",
  "transport",
  "timetable",
  "notices",
  "financial_reports"
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];
export type ModuleFlags = Record<ModuleKey, boolean>;

export const MODULE_LABELS: Record<ModuleKey, string> = {
  dashboard: "Dashboard",
  students: "Students / Learners",
  teachers: "Teachers / Employees",
  classes: "Classes",
  results: "Results / Assessments",
  report_cards: "Report Cards",
  attendance: "Attendance",
  fees: "Fees & Payments",
  payroll: "Payroll",
  messaging: "Messaging",
  uniform_orders: "Uniform Orders / Shop",
  library: "Library",
  expenses: "School Expenses",
  transport: "Transport",
  timetable: "Timetable",
  notices: "Notices / Announcements",
  financial_reports: "Financial Reports"
};

export const getAllEnabledModules = (): ModuleFlags =>
  MODULE_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {} as ModuleFlags);

export const PLAN_DEFAULTS: Record<string, ModuleFlags> = {
  basic: {
    ...getAllEnabledModules(),
    messaging: false,
    uniform_orders: false,
    library: false,
    payroll: false,
    expenses: false,
    transport: false
  },
  standard: {
    ...getAllEnabledModules(),
    payroll: false
  },
  premium: {
    ...getAllEnabledModules()
  }
};

export const getPlanKey = (tier?: string | null) => {
  const value = String(tier || "").trim().toLowerCase();
  if (!value) return "premium";
  if (["basic", "trial", "starter"].includes(value)) return "basic";
  if (["standard", "paid", "growth", "pro"].includes(value)) return "standard";
  if (["enterprise", "premium"].includes(value)) return "premium";
  return "premium";
};

export const getPlanDefaults = (tier?: string | null): ModuleFlags => ({
  ...PLAN_DEFAULTS[getPlanKey(tier)]
});

export const normalizeModuleFlags = (flags?: Record<string, any> | null): ModuleFlags => {
  const source = flags && typeof flags === "object" ? flags : {};
  const resolved = getAllEnabledModules();
  MODULE_KEYS.forEach((key) => {
    if (source[key] !== undefined) {
      resolved[key] = Boolean(source[key]);
    }
  });
  return resolved;
};

export const getEnabledModuleList = (flags?: Record<string, any> | null): ModuleKey[] => {
  const resolved = normalizeModuleFlags(flags);
  return MODULE_KEYS.filter((key) => resolved[key]);
};
